import React, { useState, useRef, useEffect } from "react";
import { Play, Pause, Clock } from "lucide-react";
import axios from "axios";

const API = `/api`;
let audioTokenCache = null;

async function getAudioToken() {
  if (audioTokenCache) return audioTokenCache;
  try {
    const { data } = await axios.get(`${API}/audio/token`);
    audioTokenCache = data.token;
    setTimeout(() => { audioTokenCache = null; }, 3 * 60 * 60 * 1000);
    return data.token;
  } catch { return null; }
}

const formatDuration = (seconds) => {
  if (!seconds || seconds <= 0) return null;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m} min ${s > 0 ? `${s.toString().padStart(2, '0')}` : ''}`.trim();
};

export const AudioCard = ({ audio }) => {
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    return () => {
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const toggle = async () => {
    if (!audioRef.current) {
      setLoading(true);
      const token = await getAudioToken();
      const url = token
        ? `${API}/audio/stream/${audio.file_name}?token=${token}`
        : `${API}/audio/stream/${audio.file_name}`;
      audioRef.current = new Audio(url);
      audioRef.current.onended = () => { setPlaying(false); setProgress(0); clearInterval(intervalRef.current); };
      audioRef.current.oncanplay = () => setLoading(false);
      audioRef.current.onerror = () => { setLoading(false); setPlaying(false); };
    }
    if (playing) {
      audioRef.current.pause();
      clearInterval(intervalRef.current);
    } else {
      audioRef.current.play().catch(() => {});
      intervalRef.current = setInterval(() => {
        if (audioRef.current && audioRef.current.duration) {
          setProgress((audioRef.current.currentTime / audioRef.current.duration) * 100);
        }
      }, 500);
    }
    setPlaying(!playing);
  };

  const duration = formatDuration(audio.duration);

  return (
    <div className="bg-white rounded-3xl border border-[#EBE7E0] p-4 hover:shadow-[0_10px_40px_rgba(44,53,49,0.08)] transition-all duration-300" data-testid={`audio-card-${audio.id}`}>
      <div className="flex items-center gap-4">
        <button onClick={toggle} disabled={loading} className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 transition-all duration-300 ${playing ? 'bg-[#D9A892]' : 'bg-[#6B8576] hover:bg-[#546A5E]'} disabled:opacity-50`} data-testid={`audio-play-${audio.id}`}>
          {playing ? <Pause className="w-5 h-5 text-white" strokeWidth={1.5} /> : <Play className="w-5 h-5 text-white ml-0.5" strokeWidth={1.5} />}
        </button>
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-[#2C3531] truncate" style={{ fontFamily: 'Outfit' }}>{audio.title}</h4>
          <div className="flex items-center gap-3 mt-0.5">
            {audio.description && <p className="text-sm text-[#6A7771] truncate">{audio.description}</p>}
            {duration && (
              <span className="flex items-center gap-1 text-xs text-[#6A7771] flex-shrink-0">
                <Clock className="w-3 h-3" strokeWidth={1.5} />{duration}
              </span>
            )}
          </div>
        </div>
      </div>
      {playing && (
        <div className="mt-3 h-1 bg-[#EBE7E0] rounded-full overflow-hidden">
          <div className="h-full bg-[#D9A892] rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>
      )}
    </div>
  );
};
